const DEFAULT_MAX_BYTES = 300 * 1024;
const DEFAULT_MAX_LONG_SIDE_PX = 800;
const DEFAULT_MIN_LONG_SIDE_PX = 240;
const DEFAULT_SCALE_STEP = 0.9;

const JPEG_QUALITY_STEPS = [
    0.92, 0.88, 0.84, 0.8, 0.76, 0.72, 0.68, 0.64,
    0.6, 0.56, 0.52, 0.48, 0.44, 0.4, 0.36, 0.32
];

const normalizeImageMimeType = (type = '') => {
    const lower = String(type).toLowerCase();
    if (lower === 'image/png') return 'image/png';
    if (lower === 'image/jpg' || lower === 'image/jpeg') return 'image/jpeg';
    return 'image/jpeg';
};

export const isAllowedImageFileType = (file) => {
    const type = (file?.type || '').toLowerCase();
    return type === 'image/png' || type === 'image/jpeg' || type === 'image/jpg';
};

export const estimateDataUrlBytes = (dataUrl) => {
    if (typeof dataUrl !== 'string') return 0;
    const parts = dataUrl.split(',');
    if (parts.length < 2) return 0;
    const base64 = parts[1];
    const padding = (base64.match(/=+$/) || [''])[0].length;
    return Math.max(0, Math.floor((base64.length * 3) / 4) - padding);
};

const loadImageFileAsDataUrl = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(new Error('No se pudo leer el archivo.'));
        reader.readAsDataURL(file);
    });

const loadImageFromDataUrl = (dataUrl) =>
    new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = () => reject(new Error('No se pudo procesar la imagen.'));
        image.src = dataUrl;
    });

const renderImageToDataUrl = (img, width, height, mimeType, quality = 1) => {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
        throw new Error('No se pudo crear el contexto de imagen.');
    }
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, 0, 0, width, height);
    if (mimeType === 'image/jpeg') {
        return canvas.toDataURL('image/jpeg', quality);
    }
    return canvas.toDataURL('image/png');
};

const resizeImageDataUrl = async (dataUrl, mimeType, maxLongSidePx) => {
    const img = await loadImageFromDataUrl(dataUrl);
    const srcWidth = Number(img.naturalWidth || img.width || 0);
    const srcHeight = Number(img.naturalHeight || img.height || 0);
    if (!srcWidth || !srcHeight) {
        throw new Error('No se pudo obtener el tamano de la imagen.');
    }

    const longSide = Math.max(srcWidth, srcHeight);
    const scale = longSide > maxLongSidePx ? (maxLongSidePx / longSide) : 1;
    const targetWidth = Math.max(1, Math.round(srcWidth * scale));
    const targetHeight = Math.max(1, Math.round(srcHeight * scale));

    return renderImageToDataUrl(img, targetWidth, targetHeight, mimeType, 1);
};

const compressImageDataUrlToTargetBytes = async (dataUrl, mimeType, targetBytes) => {
    const img = await loadImageFromDataUrl(dataUrl);
    let width = Number(img.naturalWidth || img.width || 0);
    let height = Number(img.naturalHeight || img.height || 0);
    if (!width || !height) {
        return dataUrl;
    }

    const qualitySteps = mimeType === 'image/jpeg' ? JPEG_QUALITY_STEPS : [1];
    let bestDataUrl = dataUrl;
    let bestBytes = estimateDataUrlBytes(dataUrl);

    while (true) {
        for (const quality of qualitySteps) {
            const candidate = renderImageToDataUrl(img, width, height, mimeType, quality);
            const bytes = estimateDataUrlBytes(candidate);
            if (bytes < bestBytes) {
                bestBytes = bytes;
                bestDataUrl = candidate;
            }
            if (bytes <= targetBytes) {
                return candidate;
            }
        }

        const longSide = Math.max(width, height);
        if (longSide <= DEFAULT_MIN_LONG_SIDE_PX) {
            break;
        }

        const nextWidth = Math.max(1, Math.round(width * DEFAULT_SCALE_STEP));
        const nextHeight = Math.max(1, Math.round(height * DEFAULT_SCALE_STEP));
        if (nextWidth === width && nextHeight === height) {
            break;
        }
        width = nextWidth;
        height = nextHeight;
    }

    return bestDataUrl;
};

export const optimizeImageUploadFile = async (file, options = {}) => {
    const maxBytes = Number(options.maxBytes) > 0 ? Number(options.maxBytes) : DEFAULT_MAX_BYTES;
    const maxLongSidePx = Number(options.maxLongSidePx) > 0 ? Number(options.maxLongSidePx) : DEFAULT_MAX_LONG_SIDE_PX;

    if (!file) {
        throw new Error('No se recibio ningun archivo.');
    }

    const originalBytes = Number(file.size || 0);
    const mimeType = normalizeImageMimeType(file.type);
    let dataUrl = await loadImageFileAsDataUrl(file);

    if (originalBytes > maxBytes) {
        dataUrl = await resizeImageDataUrl(dataUrl, mimeType, maxLongSidePx);
    }

    let finalBytes = estimateDataUrlBytes(dataUrl);
    if (finalBytes > maxBytes) {
        dataUrl = await compressImageDataUrlToTargetBytes(dataUrl, mimeType, maxBytes);
        finalBytes = estimateDataUrlBytes(dataUrl);
    }

    return {
        dataUrl,
        originalBytes,
        finalBytes,
        maxBytes,
        mimeType,
        exceedsLimit: finalBytes > maxBytes
    };
};
